<script setup>
import Layout from "../../../layouts/main.vue";
import { onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute } from "vue-router";
import { useDynamicConfig } from "./useDynamicConfig";

const route = useRoute();

const {
  // State
  loading,
  configTables,
  showEditDialog,
  editValues,
  editDialogTitle,
  collapsedTables,
  searchKeyword,
  paginationState,

  // Helpers
  getDisplayName,
  getColumnLabel,
  getIconUrl,
  hasDefaultValue,
  hasSearchableColumns,
  hasPaging,
  getPagedData,

  // Actions
  toggleCollapse,
  handleSort,
  handleSearch,
  handlePageChange,
  handlePageSizeChange,
  fetchPageData,
  openEditDialog,
  saveEdit,
  handleReset,
  resetState,
  clearBreadcrumb,
} = useDynamicConfig();

onMounted(async () => {
  await fetchPageData();
});

onBeforeUnmount(() => {
  clearBreadcrumb();
});

// Watch route params id thay đổi để render lại dữ liệu
watch(
  () => route.params.id,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      resetState();
      await fetchPageData();
    }
  },
);
</script>

<template>
  <Layout>
    <div v-loading="loading" style="min-height: 60vh">
      <!-- Từng bảng cấu hình -->
      <div v-for="table in configTables" :key="table.id" class="tab-card-content mb-4">
        <div class="tab-full-title" style="display: flex; align-items: center; justify-content: space-between;">
          <div style="display: flex; align-items: center;">
            <img
              v-if="table.icon"
              :src="getIconUrl(table.icon)"
              alt="icon"
              height="20"
              width="20"
              class="me-1"
            />
            <img
              v-else
              src="@/assets/icon/none-icon-svg.svg"
              alt=""
              height="20"
              width="20"
              class="me-1"
            />
            {{ getDisplayName(table) }}
            <img
              src="@/assets/icon/mini-card.svg"
              alt="collapse"
              height="18"
              width="18"
              class="collapse-icon ms-2"
              :class="{ 'collapsed': collapsedTables[table.id] }"
              style="cursor: pointer; transition: transform 0.3s ease;"
              @click="toggleCollapse(table.id)"
            />
          </div>
          <div v-if="hasSearchableColumns(table)" class="table-search-box">
            <input
              type="text"
              class="table-search-input"
              :placeholder="$t('t-dynamic-config-search-placeholder')"
              v-model="searchKeyword[table.id]"
              @keyup.enter="handleSearch(table)"
            />
            <i
              class="ri-search-line table-search-icon"
              @click="handleSearch(table)"
            ></i>
          </div>
        </div>
        <div v-show="!collapsedTables[table.id]" class="table-wrapper">
          <el-table
            :data="getPagedData(table)"
            style="width: 100%"
            class="modern-table"
            border
            @sort-change="(event) => handleSort(table, event)"
          >
            <!-- STT -->
            <el-table-column
              :label="$t('t-stt')"
              width="60"
              align="center"
            >
              <template #default="scope">
                {{ hasPaging(table) && paginationState[table.id]
                  ? (paginationState[table.id].page - 1) * paginationState[table.id].pageSize + scope.$index + 1
                  : scope.$index + 1
                }}
              </template>
            </el-table-column>
            <!-- Các cột từ columns -->
            <el-table-column
              v-for="col in table.columns"
              :key="col.code"
              :prop="col.code"
              :label="getColumnLabel(col)"
              header-align="center"
              :align="col.text_align || 'left'"
              :min-width="col.width || 120"
              :sortable="col.allow_sort ? 'custom' : false"
              show-overflow-tooltip
            >
              <template v-if="col.tooltip" #header>
                <el-tooltip effect="dark" placement="top" raw-content :content="col.tooltip.replace(/\\n/g, '<br>')">
                  <span>{{ getColumnLabel(col) }}</span>
                </el-tooltip>
              </template>
            </el-table-column>
            <!-- Cột thao tác -->
            <el-table-column
              :label="$t('t-action')"
              width="120"
              align="center"
              fixed="right"
            >
              <template #default="scope">
                <div class="btn-group-thao-tac">
                  <el-tooltip
                    effect="light"
                    :content="$t('action_option')"
                    placement="top"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-success waves-effect waves-light btn-sm"
                      @click="openEditDialog(table, scope.row)"
                    >
                      <i class="ri-edit-box-line"></i>
                    </button>
                  </el-tooltip>
                  <el-tooltip
                    v-if="hasDefaultValue(table)"
                    effect="light"
                    :content="$t('t-dynamic-config-reset-default')"
                    placement="top"
                  >
                    <button
                      type="button"
                      class="btn btn-soft-warning waves-effect waves-light btn-sm"
                      @click="handleReset(table, scope.row)"
                    >
                      <i class="ri-refresh-line"></i>
                    </button>
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="hasPaging(table)" class="pagination-wrapper mt-3">
            <el-pagination
              small
              background
              v-model:current-page="paginationState[table.id].page"
              v-model:page-size="paginationState[table.id].pageSize"
              :total="table.tableData.length"
              layout="total, sizes, prev, pager, next"
              :page-sizes="[10, 20, 50]"
              @current-change="handlePageChange(table, $event)"
              @size-change="handlePageSizeChange(table, $event)"
            />
          </div>
        </div>
      </div>

      <!-- Không có bảng nào -->
      <div
        v-if="!loading && configTables.length === 0"
        class="text-muted text-center py-5"
      >
        {{ $t("t-dynamic-config-no-tables") }}
      </div>
    </div>

    <!-- Dialog chỉnh sửa -->
    <el-dialog
      v-model="showEditDialog"
      :title="editDialogTitle"
      :width="editValues.length > 2 ? '600px' : '500px'"
      :close-on-click-modal="false"
    >
      <el-form label-position="top">
        <!-- Trường hợp 1 giá trị -->
        <template v-if="editValues.length === 1">
          <el-form-item>
            <template #label>
              {{ editValues[0].name }}
            </template>
            <el-input-number
              v-if="
                editValues[0].type === 'integer' ||
                editValues[0].type === 'long' ||
                editValues[0].type === 'double' || 
                (editValues[0].type && editValues[0].type.toLowerCase() === 'int')
              "
              v-model="editValues[0].value"
              :controls="false"
              size="large"
              style="width: 100%"
            ></el-input-number>
            <el-input
              v-else
              v-model="editValues[0].value"
              size="large"
            ></el-input>
            <div
              v-if="editValues[0].min != null || editValues[0].max != null"
              class="min-max-info"
            >
              <span v-if="editValues[0].min != null">Min: {{ editValues[0].min }}</span>
              <span v-if="editValues[0].min != null && editValues[0].max != null" class="min-max-spacer"></span>
              <span v-if="editValues[0].max != null">Max: {{ editValues[0].max }}</span>
            </div>
            <div v-if="editValues[0].errors && editValues[0].errors.length > 0" class="input-error-list">
              <div v-for="(err, idx) in editValues[0].errors" :key="idx" class="input-error-item">
                <i class="ri-close-circle-fill error-icon"></i>
                <span>{{ err }}</span>
              </div>
            </div>
          </el-form-item>
        </template>
        <!-- Trường hợp 2 giá trị: hiển thị ngang hàng -->
        <template v-else-if="editValues.length === 2">
          <div class="d-flex gap-3">
            <div style="flex: 1">
              <el-form-item>
                <template #label>{{ editValues[0].name }}</template>
                <el-input-number
                  v-if="
                    editValues[0].type === 'integer' ||
                    editValues[0].type === 'long' ||
                    editValues[0].type === 'double' || 
                    (editValues[0].type && editValues[0].type.toLowerCase() === 'int')
                  "
                  v-model="editValues[0].value"
                  :controls="false"
                  size="large"
                  style="width: 100%"
                ></el-input-number>
                <el-input
                  v-else
                  v-model="editValues[0].value"
                  size="large"
                ></el-input>
                <div
                  v-if="editValues[0].min != null || editValues[0].max != null"
                  class="min-max-info"
                >
                  <span v-if="editValues[0].min != null">Min: {{ editValues[0].min }}</span>
                  <span v-if="editValues[0].min != null && editValues[0].max != null" class="min-max-spacer"></span>
                  <span v-if="editValues[0].max != null">Max: {{ editValues[0].max }}</span>
                </div>
                <div v-if="editValues[0].errors && editValues[0].errors.length > 0" class="input-error-list">
                  <div v-for="(err, idx) in editValues[0].errors" :key="idx" class="input-error-item">
                    <i class="ri-close-circle-fill error-icon"></i>
                    <span>{{ err }}</span>
                  </div>
                </div>
              </el-form-item>
            </div>
            <div style="flex: 1">
              <el-form-item>
                <template #label>{{ editValues[1].name }}</template>
                <el-input-number
                  v-if="
                    editValues[1].type === 'integer' ||
                    editValues[1].type === 'long' ||
                    editValues[1].type === 'double' ||
                    (editValues[1].type && editValues[1].type.toLowerCase() === 'int')
                  "
                  v-model="editValues[1].value"
                  :controls="false"
                  size="large"
                  style="width: 100%"
                ></el-input-number>
                <el-input
                  v-else
                  v-model="editValues[1].value"
                  size="large"
                ></el-input>
                <div
                  v-if="editValues[1].min != null || editValues[1].max != null"
                  class="min-max-info"
                >
                  <span v-if="editValues[1].min != null">Min: {{ editValues[1].min }}</span>
                  <span v-if="editValues[1].min != null && editValues[1].max != null" class="min-max-spacer"></span>
                  <span v-if="editValues[1].max != null">Max: {{ editValues[1].max }}</span>
                </div>
                <div v-if="editValues[1].errors && editValues[1].errors.length > 0" class="input-error-list">
                  <div v-for="(err, idx) in editValues[1].errors" :key="idx" class="input-error-item">
                    <i class="ri-close-circle-fill error-icon"></i>
                    <span>{{ err }}</span>
                  </div>
                </div>
              </el-form-item>
            </div>
          </div>
        </template>
        <!-- Trường hợp nhiều hơn 2 giá trị: chia layout 2 cột -->
        <template v-else>
          <div class="edit-values-grid">
            <div
              v-for="(item, idx) in editValues"
              :key="idx"
              class="edit-value-col"
            >
              <el-form-item>
                <template #label>{{ item.name }}</template>
                <el-input-number
                  v-if="
                    item.type === 'integer' ||
                    item.type === 'long' ||
                    item.type === 'double' ||
                    (item.type && item.type.toLowerCase() === 'int')
                  "
                  v-model="item.value"
                  :controls="false"
                  size="large"
                  style="width: 100%"
                ></el-input-number>
                <el-input
                  v-else
                  v-model="item.value"
                  size="large"
                ></el-input>
                <div
                  v-if="item.min != null || item.max != null"
                  class="min-max-info"
                >
                  <span v-if="item.min != null">Min: {{ item.min }}</span>
                  <span
                    v-if="item.min != null && item.max != null"
                    class="min-max-spacer"
                  ></span>
                  <span v-if="item.max != null">Max: {{ item.max }}</span>
                </div>
                <div
                  v-if="item.errors && item.errors.length > 0"
                  class="input-error-list"
                >
                  <div
                    v-for="(err, errIdx) in item.errors"
                    :key="errIdx"
                    class="input-error-item"
                  >
                    <i class="ri-close-circle-fill error-icon"></i>
                    <span>{{ err }}</span>
                  </div>
                </div>
              </el-form-item>
            </div>
          </div>
        </template>
      </el-form>
      <template #footer>
        <div class="text-center">
          <button class="btn btn-secondary btn-border" @click="saveEdit">
            {{ $t("t-dynamic-config-save") }}
          </button>
        </div>
      </template>
    </el-dialog>
  </Layout>
</template>

<style lang="scss" scoped src="./dynamic-config.scss"></style>
